import React, { useState, useEffect, useRef } from "react";
import { Link, useNavigate, useLocation } from "react-router-dom";
import { ShoppingCart, Heart, Sun, Moon, Menu, X, User, LogOut, Settings } from "lucide-react";
import { useAuth } from "../contexts/AuthContext";
import { useCart } from "../contexts/CartContext";
import { useLanguage } from "../contexts/LanguageContext";
import { useTheme } from "../contexts/ThemeContext";
import { motion, AnimatePresence } from "framer-motion";

export default function Navbar() {
  const { user, logout } = useAuth();
  const { count } = useCart();
  const { lang, toggleLang, t } = useLanguage();
  const { theme, toggleTheme } = useTheme();
  const navigate = useNavigate();
  const location = useLocation();
  const [menuOpen, setMenuOpen] = useState(false);
  const [userMenuOpen, setUserMenuOpen] = useState(false);
  const [scrolled, setScrolled] = useState(false);
  const userMenuRef = useRef(null);

  useEffect(() => {
    const onScroll = () => setScrolled(window.scrollY > 20);
    window.addEventListener("scroll", onScroll);
    return () => window.removeEventListener("scroll", onScroll);
  }, []);

  useEffect(() => {
    const handler = (e) => {
      if (userMenuRef.current && !userMenuRef.current.contains(e.target)) {
        setUserMenuOpen(false);
      }
    };
    document.addEventListener("mousedown", handler);
    return () => document.removeEventListener("mousedown", handler);
  }, []);

  useEffect(() => { setMenuOpen(false); }, [location]);

  const handleLogout = async () => {
    await logout();
    navigate("/");
    setUserMenuOpen(false);
  };

  const navLinks = [
    { to: "/", label: t("nav.showroom") },
    { to: "/shop", label: t("nav.shop") },
    { to: "/about", label: t("nav.about") },
    { to: "/contact", label: t("nav.contact") },
  ];

  const isActive = (path) => location.pathname === path;

  return (
    <nav
      data-testid="navbar"
      className={`fixed top-0 left-0 right-0 z-50 transition-all duration-500 ${
        scrolled
          ? "bg-background/80 backdrop-blur-xl border-b border-border/60 shadow-lg shadow-background/20"
          : "bg-transparent"
      }`}
    >
      <div className="max-w-7xl mx-auto px-6 py-4 flex items-center justify-between">
        {/* Brand */}
        <Link to="/" className="font-heading text-xl font-bold tracking-tighter text-foreground hover:opacity-80 transition-opacity">
          LIRK<span className="text-foreground/40 font-light"> PRINT</span>
        </Link>

        {/* Desktop Nav */}
        <div className="hidden md:flex items-center gap-8">
          {navLinks.map(link => (
            <Link
              key={link.to}
              to={link.to}
              className={`text-sm font-mono tracking-wider uppercase transition-all duration-300 ${
                isActive(link.to)
                  ? "text-foreground"
                  : "text-foreground/50 hover:text-foreground"
              }`}
            >
              {link.label}
            </Link>
          ))}
        </div>

        {/* Controls */}
        <div className="flex items-center gap-3">
          {/* Language Toggle */}
          <button
            data-testid="language-toggle"
            onClick={toggleLang}
            aria-label="Toggle language"
            className="hidden md:flex items-center text-xs font-mono tracking-widest text-foreground/50 hover:text-foreground transition-colors uppercase"
          >
            {lang === "de" ? "EN" : "DE"}
          </button>

          {/* Theme Toggle */}
          <button
            data-testid="theme-toggle"
            onClick={toggleTheme}
            className="p-2 rounded-sm text-foreground/50 hover:text-foreground transition-all duration-300 hover:bg-foreground/5"
            aria-label="Toggle theme"
          >
            {theme === "dark" ? <Sun size={18} strokeWidth={1.5} /> : <Moon size={18} strokeWidth={1.5} />}
          </button>

          {/* Cart */}
          <Link
            to="/cart"
            data-testid="cart-icon"
            className="relative p-2 rounded-sm text-foreground/50 hover:text-foreground transition-all duration-300 hover:bg-foreground/5"
          >
            <ShoppingCart size={18} strokeWidth={1.5} />
            {count > 0 && (
              <span
                data-testid="cart-count"
                className="absolute -top-0.5 -right-0.5 w-4 h-4 bg-foreground text-background text-[10px] rounded-full flex items-center justify-center font-bold"
              >
                {count}
              </span>
            )}
          </Link>

          {/* User Menu */}
          {user ? (
            <div className="relative" ref={userMenuRef}>
              <button
                data-testid="user-menu-btn"
                onClick={() => setUserMenuOpen(!userMenuOpen)}
                className="p-2 rounded-sm text-foreground/50 hover:text-foreground transition-all duration-300 hover:bg-foreground/5"
              >
                <User size={18} strokeWidth={1.5} />
              </button>
              <AnimatePresence>
                {userMenuOpen && (
                  <motion.div
                    initial={{ opacity: 0, y: -8, scale: 0.96 }}
                    animate={{ opacity: 1, y: 0, scale: 1 }}
                    exit={{ opacity: 0, y: -8, scale: 0.96 }}
                    transition={{ duration: 0.15 }}
                    className="absolute right-0 mt-2 w-48 bg-surface border border-border rounded-sm shadow-2xl overflow-hidden"
                  >
                    <div className="px-4 py-3 border-b border-border">
                      <p className="text-xs text-foreground/50 font-mono">{user.email}</p>
                      <p className="text-sm font-medium text-foreground">{user.name}</p>
                    </div>
                    <Link to="/profile" onClick={() => setUserMenuOpen(false)} className="flex items-center gap-3 px-4 py-3 text-sm text-foreground/70 hover:text-foreground hover:bg-foreground/5 transition-colors">
                      <User size={14} strokeWidth={1.5} /> {t("nav.profile")}
                    </Link>
                    <Link to="/wishlist" onClick={() => setUserMenuOpen(false)} className="flex items-center gap-3 px-4 py-3 text-sm text-foreground/70 hover:text-foreground hover:bg-foreground/5 transition-colors">
                      <Heart size={14} strokeWidth={1.5} /> {t("nav.wishlist")}
                    </Link>
                    {user.role === "admin" && (
                      <Link to="/admin" onClick={() => setUserMenuOpen(false)} className="flex items-center gap-3 px-4 py-3 text-sm text-foreground/70 hover:text-foreground hover:bg-foreground/5 transition-colors">
                        <Settings size={14} strokeWidth={1.5} /> {t("nav.admin")}
                      </Link>
                    )}
                    <button onClick={handleLogout} className="w-full flex items-center gap-3 px-4 py-3 text-sm text-foreground/70 hover:text-foreground hover:bg-foreground/5 transition-colors border-t border-border">
                      <LogOut size={14} strokeWidth={1.5} /> {t("nav.logout")}
                    </button>
                  </motion.div>
                )}
              </AnimatePresence>
            </div>
          ) : (
            <Link
              to="/auth"
              data-testid="login-btn"
              className="hidden md:flex px-4 py-2 text-xs font-mono tracking-wider uppercase border border-border text-foreground/70 hover:text-foreground hover:border-foreground/50 transition-all duration-300 rounded-sm"
            >
              {t("nav.login")}
            </Link>
          )}

          {/* Mobile Menu Toggle */}
          <button
            data-testid="mobile-menu-btn"
            onClick={() => setMenuOpen(!menuOpen)}
            className="md:hidden p-2 text-foreground/50 hover:text-foreground transition-colors"
          >
            {menuOpen ? <X size={20} strokeWidth={1.5} /> : <Menu size={20} strokeWidth={1.5} />}
          </button>
        </div>
      </div>

      {/* Mobile Menu */}
      <AnimatePresence>
        {menuOpen && (
          <motion.div
            initial={{ opacity: 0, height: 0 }}
            animate={{ opacity: 1, height: "auto" }}
            exit={{ opacity: 0, height: 0 }}
            className="md:hidden bg-background/95 backdrop-blur-xl border-t border-border overflow-hidden"
          >
            <div className="px-6 py-6 space-y-4">
              {navLinks.map(link => (
                <Link
                  key={link.to}
                  to={link.to}
                  className="block text-sm font-mono tracking-wider uppercase text-foreground/60 hover:text-foreground transition-colors"
                >
                  {link.label}
                </Link>
              ))}
              <div className="flex items-center gap-4 pt-4 border-t border-border">
                <button onClick={toggleLang} className="text-xs font-mono tracking-widest text-foreground/50 hover:text-foreground transition-colors uppercase">
                  {lang === "de" ? "EN" : "DE"}
                </button>
                {!user && (
                  <Link to="/auth" className="text-sm font-mono tracking-wider uppercase text-foreground/60 hover:text-foreground transition-colors">
                    {t("nav.login")}
                  </Link>
                )}
              </div>
            </div>
          </motion.div>
        )}
      </AnimatePresence>
    </nav>
  );
}
